import { Button, ClickAwayListener, Typography } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Tooltip from "@mui/material/Tooltip";
import withStyles from "@mui/styles/withStyles";
import { useAppSelector } from "app/hooks";
import { OLIcon } from "components/CustomIcons/OLIcon";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { getAcronym } from "utilities/strings";
import { isGlobalManagementView } from "../LinksList/utils";
import { StoreList } from "./StoreList";
import { useStyles } from "./StoreSelect.styles";

const AdditionalMenu = withStyles((theme: OLTheme) => ({
  tooltip: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
    backgroundColor: theme.white00,
    boxShadow:
      "0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
    maxHeight: "90vh",
    overflowX: "hidden",
    // firefox
    scrollbarColor: `#17132D ${theme.primary700}`,
    scrollbarWidth: "thin",

    // chrome, safari
    "&::-webkit-scrollbar-button": {
      display: "none",
    },
    "&::-webkit-scrollbar": {
      backgroundColor: theme.primary700,
      width: "10px",
    },
    "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
      borderRadius: 8,
      backgroundColor: "#17132D",
    },
  },
  arrow: {
    color: theme.primary700,
  },
}))(Tooltip);

export function StoreSelect(props: { isDrawerOpen?: boolean }) {
  const classes = useStyles({ isDrawerOpen: props.isDrawerOpen });
  const store = useAppSelector(selectCurrentStoreData);
  const location = useLocation();
  const { message } = useMessage();

  const [isAdditionalMenuOpen, setIsAdditionalMenuOpen] = useState(false);

  const handleAdditionalMenuOpen = () => {
    setIsAdditionalMenuOpen(true);
  };

  const handleAdditionalMenuClose = () => {
    setIsAdditionalMenuOpen(false);
  };

  const isGlobalManagementActive = isGlobalManagementView(location.pathname);

  return (
    <FormControl
      className={`${classes.storeWrapper} ${isGlobalManagementActive && props.isDrawerOpen ? classes.globalManagementWrapper : ""}`}
      variant="standard"
    >
      <OLIcon isDrawerOpen={false} />
      {isGlobalManagementActive ? (
        <div
          className={!props.isDrawerOpen ? classes.storeNameButtonSmall : ""}
        >
          <Typography
            className={classes.globalAdmin}
            ml={props.isDrawerOpen ? 6 : 0}
          >
            {props.isDrawerOpen
              ? message({ id: "globalAdmin" })
              : getAcronym(message({ id: "globalAdmin" }))}
          </Typography>
        </div>
      ) : props.isDrawerOpen ? (
        <ClickAwayListener onClickAway={handleAdditionalMenuClose}>
          <div className={classes.buttonPosition}>
            <AdditionalMenu
              arrow
              onClose={handleAdditionalMenuClose}
              open={isAdditionalMenuOpen}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              placement="right-start"
              title={
                <StoreList
                  handleAdditionalMenuClose={handleAdditionalMenuClose}
                  handleAdditionalMenuOpen={handleAdditionalMenuOpen}
                  isDrawerOpen={props.isDrawerOpen}
                  currentStore={store.code}
                />
              }
            >
              <Button
                className={classes.storeNameButton}
                onClick={handleAdditionalMenuOpen}
                data-test-id="select-tenant-button"
              >
                <div
                  className={classes.storeCode}
                  data-test-id="tenant-current"
                >
                  {store?.name}
                </div>
                <div className={classes.dropdownIconWrapper}>
                  <ChevronDownIcon />
                </div>
              </Button>
            </AdditionalMenu>
          </div>
        </ClickAwayListener>
      ) : (
        <ClickAwayListener onClickAway={handleAdditionalMenuClose}>
          <div>
            <AdditionalMenu
              arrow
              onClose={handleAdditionalMenuClose}
              open={isAdditionalMenuOpen}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              placement="right-start"
              title={
                <StoreList
                  handleAdditionalMenuClose={handleAdditionalMenuClose}
                  handleAdditionalMenuOpen={handleAdditionalMenuOpen}
                  isDrawerOpen={props.isDrawerOpen}
                  currentStore={store.code}
                />
              }
            >
              <div
                className={classes.storeNameButtonSmall}
                onClick={handleAdditionalMenuOpen}
                data-test-id="select-tenant-button"
              >
                {getAcronym(store?.name)}
              </div>
            </AdditionalMenu>
          </div>
        </ClickAwayListener>
      )}
    </FormControl>
  );
}
